import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import './RouteMap.css';

// Fix para ícones do Leaflet usando imagens da pasta public
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/leaflet/marker-icon-2x.png',
  iconUrl: '/images/leaflet/marker-icon.png',
  shadowUrl: '/images/leaflet/marker-shadow.png',
});

// Ícones customizados para diferentes tipos de checkpoints
const origemIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const destinoIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const pedagioIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const pausaIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const fimJornadaIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const pausaObrigatoriaIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

interface Checkpoint {
  id?: number;
  tipo?: 'origem' | 'destino' | 'pedagio' | 'pausa' | 'fim_jornada' | 'pausa_obrigatoria';
  latitude: number;
  longitude: number;
  nome?: string;
  ordem: number;
  duracao_parada?: number;
  distancia_ate_proximo?: number;
  tempo_ate_proximo?: number;
  valor?: number; // Valor do pedágio, se existir
  rodovia?: string; // Rodovia do pedágio, se existir
}

interface RouteMapProps {
  checkpoints: Checkpoint[];
  routeGeometry?: number[][];
  onCheckpointsChange?: (checkpoints: Checkpoint[]) => void;
  editable?: boolean;
  showCalculateButton?: boolean;
  onCalculateRoute?: () => void;
  calculating?: boolean;
}

// Componente para adicionar checkpoints clicando no mapa
function MapClickHandler({ onAddCheckpoint, enabled }: { onAddCheckpoint: (lat: number, lng: number) => void, enabled: boolean }) {
  useMapEvents({
    click(e) {
      if (enabled) {
        onAddCheckpoint(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
}

const RouteMap: React.FC<RouteMapProps> = ({ 
  checkpoints, 
  routeGeometry, 
  onCheckpointsChange,
  editable = false,
  showCalculateButton = false,
  onCalculateRoute,
  calculating = false
}) => {
  const [editMode, setEditMode] = useState(false);
  const [addingCheckpointType, setAddingCheckpointType] = useState<Checkpoint['tipo'] | null>(null);

  // Calcula o centro do mapa baseado nos checkpoints
  const getMapCenter = (): [number, number] => {
    if (checkpoints.length === 0) {
      return [-23.5505, -46.6333]; // São Paulo como padrão
    }
    const avgLat = checkpoints.reduce((sum, cp) => sum + Number(cp.latitude), 0) / checkpoints.length;
    const avgLng = checkpoints.reduce((sum, cp) => sum + Number(cp.longitude), 0) / checkpoints.length;
    return [avgLat, avgLng];
  };

  const handleAddCheckpoint = (lat: number, lng: number) => {
    if (!addingCheckpointType || !onCheckpointsChange) return;

    const newCheckpoint: Checkpoint = {
      tipo: addingCheckpointType,
      latitude: lat,
      longitude: lng,
      nome: `${addingCheckpointType.charAt(0).toUpperCase() + addingCheckpointType.slice(1)} ${checkpoints.length + 1}`,
      ordem: checkpoints.length + 1,
      duracao_parada: addingCheckpointType === 'pausa' ? 30 : undefined
    };

    onCheckpointsChange([...checkpoints, newCheckpoint]);
    setAddingCheckpointType(null);
  };

  const handleRemoveCheckpoint = (index: number) => {
    if (!onCheckpointsChange) return;
    const updated = checkpoints.filter((_, i) => i !== index);
    // Reordenar
    updated.forEach((cp, i) => cp.ordem = i + 1);
    onCheckpointsChange(updated);
  };

  const handleMoveCheckpoint = (index: number, direction: 'up' | 'down') => {
    if (!onCheckpointsChange) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= checkpoints.length) return;

    const updated = [...checkpoints];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    // Reordenar
    updated.forEach((cp, i) => cp.ordem = i + 1);
    onCheckpointsChange(updated);
  };

  const getIconForCheckpoint = (tipo: Checkpoint['tipo']) => {
    switch (tipo) {
      case 'origem': return origemIcon;
      case 'destino': return destinoIcon;
      case 'pedagio': return pedagioIcon;
      case 'pausa': return pausaIcon;
      case 'pausa_obrigatoria': return pausaObrigatoriaIcon;
      case 'fim_jornada': return fimJornadaIcon;
      default: return undefined;
    }
  };

  const getTipoLabel = (tipo: Checkpoint['tipo']) => {
    if (!tipo) return 'Checkpoint';
    const labels = {
      origem: 'Origem',
      destino: 'Destino',
      pedagio: 'Pedágio',
      pausa: 'Pausa',
      fim_jornada: 'Fim de Jornada',
      pausa_obrigatoria: 'Pausa Obrigatória'
    };
    return labels[tipo] || 'Checkpoint';
  };

  return (
    <div className="route-map-container">
      {editable && (
        <div className="route-map-controls">
          <div className="control-group">
            <button 
              className={`btn-control ${editMode ? 'active' : ''}`}
              onClick={() => setEditMode(!editMode)}
            >
              {editMode ? '✓ Modo Edição' : '✏️ Editar Rota'}
            </button>
          </div>

          {editMode && (
            <div className="control-group">
              <span className="control-label">Adicionar:</span>
              <button 
                className={`btn-add-checkpoint ${addingCheckpointType === 'origem' ? 'active' : ''}`}
                onClick={() => setAddingCheckpointType(addingCheckpointType === 'origem' ? null : 'origem')}
              >
                <span className="checkpoint-dot" role="img" aria-label="origem">🟢</span>
                <span className="checkpoint-label">Origem</span>
              </button>
              <button 
                className={`btn-add-checkpoint ${addingCheckpointType === 'destino' ? 'active' : ''}`}
                onClick={() => setAddingCheckpointType(addingCheckpointType === 'destino' ? null : 'destino')}
              >
                <span className="checkpoint-dot" role="img" aria-label="destino">🔴</span>
                <span className="checkpoint-label">Destino</span>
              </button>
              <button 
                className={`btn-add-checkpoint ${addingCheckpointType === 'pedagio' ? 'active' : ''}`}
                onClick={() => setAddingCheckpointType(addingCheckpointType === 'pedagio' ? null : 'pedagio')}
              >
                <span className="checkpoint-dot" role="img" aria-label="pedagio">🟠</span>
                <span className="checkpoint-label">Pedágio</span>
              </button>
              <button 
                className={`btn-add-checkpoint ${addingCheckpointType === 'pausa' ? 'active' : ''}`}
                onClick={() => setAddingCheckpointType(addingCheckpointType === 'pausa' ? null : 'pausa')}
              >
                <span className="checkpoint-dot" role="img" aria-label="pausa">🔵</span>
                <span className="checkpoint-label">Pausa</span>
              </button>
              <button 
                className={`btn-add-checkpoint ${addingCheckpointType === 'fim_jornada' ? 'active' : ''}`}
                onClick={() => setAddingCheckpointType(addingCheckpointType === 'fim_jornada' ? null : 'fim_jornada')}
              >
                <span className="checkpoint-dot" role="img" aria-label="fim_jornada">🟣</span>
                <span className="checkpoint-label">Fim Jornada</span>
              </button>
            </div>
          )}

          {showCalculateButton && (
            <div className="control-group">
              <button 
                className="btn-calculate-route"
                onClick={onCalculateRoute}
                disabled={calculating || checkpoints.length < 2}
              >
                {calculating ? '⏳ Calculando...' : '🗺️ Calcular Rota'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="route-map-wrapper">
        <MapContainer
          center={getMapCenter()}
          zoom={13}
          style={{ height: '600px', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Componente para adicionar checkpoints clicando no mapa */}
          <MapClickHandler 
            onAddCheckpoint={handleAddCheckpoint} 
            enabled={editMode && addingCheckpointType !== null}
          />

          {/* Renderizar checkpoints */}
          {checkpoints.map((checkpoint, index) => {
            const lat = Number(checkpoint.latitude);
            const lng = Number(checkpoint.longitude);
            const icon = getIconForCheckpoint(checkpoint.tipo);
            return (
              <Marker 
                key={index}
                position={[lat, lng]}
                {...(icon ? { icon } : {})}
              >
                <Popup>
                  <div className="checkpoint-popup">
                    <h4>{getTipoLabel(checkpoint.tipo)}</h4>
                    <p><strong>Nome:</strong> {checkpoint.nome}</p>
                    <p><strong>Ordem:</strong> {checkpoint.ordem}</p>
                    <p><strong>Coordenadas:</strong> {lat.toFixed(6)}, {lng.toFixed(6)}</p>
                    {checkpoint.duracao_parada && (
                      <p><strong>Duração:</strong> {checkpoint.duracao_parada} min</p>
                    )}
                    {checkpoint.distancia_ate_proximo && (
                      <p><strong>Distância até próximo:</strong> {checkpoint.distancia_ate_proximo} km</p>
                    )}
                    {checkpoint.tempo_ate_proximo && (
                      <p><strong>Tempo até próximo:</strong> {Math.floor(checkpoint.tempo_ate_proximo / 60)}h {checkpoint.tempo_ate_proximo % 60}min</p>
                    )}
                    {/* Exibir valor do pedágio se for um checkpoint de pedágio */}
                    {checkpoint.tipo === 'pedagio' && (
                      <>
                        {checkpoint.valor !== undefined && (
                          <p><strong>Valor:</strong> R$ {checkpoint.valor.toFixed(2)}</p>
                        )}
                        {checkpoint.rodovia && (
                          <p><strong>Rodovia:</strong> {checkpoint.rodovia}</p>
                        )}
                      </>
                    )}
                    {editable && editMode && (
                      <div className="checkpoint-actions">
                        {index > 0 && (
                          <button onClick={() => handleMoveCheckpoint(index, 'up')}>⬆️</button>
                        )}
                        {index < checkpoints.length - 1 && (
                          <button onClick={() => handleMoveCheckpoint(index, 'down')}>⬇️</button>
                        )}
                        <button onClick={() => handleRemoveCheckpoint(index)}>🗑️</button>
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}


          {/* Renderizar geometria da rota se disponível (aceita GeoJSON ou array) */}
          {routeGeometry && (() => {
            let coords: number[][] = [];
            // Se for GeoJSON
            if (
              typeof routeGeometry === 'object' &&
              routeGeometry !== null &&
              'type' in routeGeometry &&
              routeGeometry.type === 'LineString' &&
              Array.isArray((routeGeometry as any).coordinates)
            ) {
              coords = (routeGeometry as any).coordinates;
            } else if (Array.isArray(routeGeometry) && routeGeometry.length > 0 && Array.isArray(routeGeometry[0])) {
              coords = routeGeometry as number[][];
            }
            if (coords.length > 0) {
              return (
                <Polyline
                  positions={coords.map(coord => [coord[1], coord[0]])}
                  color="blue"
                  weight={4}
                  opacity={0.7}
                />
              );
            }
            return null;
          })()}

          {/* Linha conectando os checkpoints se não houver geometria */}
          {!routeGeometry && checkpoints.length > 1 && (
            <Polyline
              positions={checkpoints.map(cp => [Number(cp.latitude), Number(cp.longitude)])}
              color="gray"
              weight={2}
              opacity={0.5}
              dashArray="5, 10"
            />
          )}
        </MapContainer>
      </div>

      {addingCheckpointType && (
        <div className="map-instruction">
          ℹ️ Clique no mapa para adicionar um checkpoint do tipo: <strong>{getTipoLabel(addingCheckpointType)}</strong>
        </div>
      )}

      {editable && checkpoints.length > 0 && (
        <div className="checkpoints-list">
          <h4>Checkpoints da Rota ({checkpoints.length})</h4>
          <ul>
            {checkpoints.map((cp, index) => (
              <li key={index} className={`checkpoint-item checkpoint-${cp.tipo}`}>
                <span className="checkpoint-order">{cp.ordem}</span>
                <span className="checkpoint-name">{cp.nome}</span>
                <span className="checkpoint-type">({getTipoLabel(cp.tipo)})</span>
                {cp.duracao_parada && <span className="checkpoint-duration">{cp.duracao_parada}min</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RouteMap;
